import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { getBaseURL } from "../../services/api";
import { ToastProvider, useToast } from "./AdminToast";
import { S, COLORS, GLOBAL_ADMIN_CSS, getRoleColor } from "./adminStyles";

const API_URL = getBaseURL();

const isValidUrl = (url) => {
  if (!url) return true; // optional field
  try { new URL(url); return true; }
  catch { return false; }
};

const ImagePreview = ({ url, name }) => {
  const [error, setError] = useState(false);
  const [loaded, setLoaded] = useState(false);

  if (!url || !isValidUrl(url)) return null;

  return (
    <div style={{ marginTop: "10px", display: "flex", alignItems: "center", gap: "10px" }}>
      {!loaded && !error && (
        <div style={{ width: "48px", height: "48px", borderRadius: "50%", background: COLORS.inputBg, border: `1px solid ${COLORS.cardBorder}`, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <span style={{ fontSize: "10px", color: COLORS.textMuted }}>...</span>
        </div>
      )}
      {!error && (
        <img
          src={url}
          alt={name || "preview"}
          onLoad={() => setLoaded(true)}
          onError={() => { setError(true); setLoaded(false); }}
          style={{ width: "48px", height: "48px", borderRadius: "50%", objectFit: "cover", border: `2px solid ${COLORS.green}`, display: loaded ? "block" : "none" }}
        />
      )}
      {loaded && (
        <span style={{ fontSize: "11px", color: COLORS.green, fontWeight: 600 }}>✓ Image loaded successfully</span>
      )}
      {error && (
        <span style={{ fontSize: "11px", color: COLORS.red, fontWeight: 600 }}>✗ Invalid image URL</span>
      )}
    </div>
  );
};

const AddPlayerInner = () => {
  const [playerData, setPlayerData] = useState({ name: "", role: "BAT", team: "", credit: 9.0, image: "" });
  const [masterPlayers, setMasterPlayers] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [filterTeam, setFilterTeam] = useState("ALL");
  const [filterRole, setFilterRole] = useState("ALL");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [csvUploading, setCsvUploading] = useState(false);
  const [csvPreview, setCsvPreview] = useState(null);
  const [showCsv, setShowCsv] = useState(false);
  const token = localStorage.getItem("adminToken");
  const navigate = useNavigate();
  const toast = useToast();
  const csvRef = useRef();

  useEffect(() => {
    if (!token) navigate("/winrox11-super-admin");
    else fetchMasterPlayers();
  }, [token, navigate]);

  const fetchMasterPlayers = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/admin/master-players`, { headers: { Authorization: `Bearer ${token}` } });
      setMasterPlayers(res.data);
    } catch (err) {
      toast.error("Failed to fetch players.");
    } finally { setLoading(false); }
  };

  const validateForm = () => {
    if (!playerData.name.trim()) { toast.warn("Player name is required."); return false; }
    if (!playerData.team.trim()) { toast.warn("Team code is required."); return false; }
    if (playerData.credit < 1 || playerData.credit > 15) { toast.warn("Credit must be between 1 and 15."); return false; }
    if (playerData.image && !isValidUrl(playerData.image)) { toast.warn("Please enter a valid image URL."); return false; }
    return true;
  };

  const handleSavePlayer = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setSaving(true);
    try {
      const payload = { ...playerData, team: playerData.team.trim().toUpperCase() };
      if (editingId) {
        await axios.put(`${API_URL}/admin/master-players/${editingId}`, payload, { headers: { Authorization: `Bearer ${token}` } });
        toast.success(`${payload.name} updated successfully!`);
      } else {
        await axios.post(`${API_URL}/admin/players`, payload, { headers: { Authorization: `Bearer ${token}` } });
        toast.success(`${payload.name} added to the database!`);
      }
      setPlayerData({ name: "", role: "BAT", team: playerData.team, credit: 9.0, image: "" });
      setEditingId(null);
      fetchMasterPlayers();
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to save player.");
    } finally { setSaving(false); }
  };

  const handleEditClick = (p) => {
    setEditingId(p.id);
    setPlayerData({ name: p.name, role: p.role, team: p.team, credit: parseFloat(p.credit), image: p.image || "" });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDeletePlayer = async (id, name) => {
    if (!window.confirm(`Remove "${name}" from Master Squad?`)) return;
    try {
      await axios.delete(`${API_URL}/admin/master-players/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`${name} removed from database.`);
      fetchMasterPlayers();
    } catch (err) {
      toast.error("Failed to delete player.");
    }
  };

  // CSV Bulk Upload
  const handleCsvChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (evt) => {
      const lines = evt.target.result.split("\n").filter(l => l.trim());
      const headers = lines[0].split(",").map(h => h.trim().toLowerCase());
      const rows = lines.slice(1).map(line => {
        const cols = line.split(",").map(c => c.trim());
        const obj = {};
        headers.forEach((h, i) => { obj[h] = cols[i] || ""; });
        return obj;
      }).filter(r => r.name);
      setCsvPreview(rows);
    };
    reader.readAsText(file);
  };

  const handleCsvUpload = async () => {
    if (!csvPreview || csvPreview.length === 0) return;
    setCsvUploading(true);
    let success = 0, failed = 0;
    for (const row of csvPreview) {
      try {
        await axios.post(`${API_URL}/admin/players`, {
          name: row.name,
          team: (row.team || "").toUpperCase(),
          role: (row.role || "BAT").toUpperCase(),
          credit: parseFloat(row.credit || row.credits || 9),
          image: row.image || row.photo_url || "",
        }, { headers: { Authorization: `Bearer ${token}` } });
        success++;
      } catch { failed++; }
    }
    toast.success(`CSV Upload: ${success} added, ${failed} failed.`);
    setCsvPreview(null);
    setShowCsv(false);
    if (csvRef.current) csvRef.current.value = "";
    fetchMasterPlayers();
    setCsvUploading(false);
  };

  // Filtered players
  const uniqueTeams = ["ALL", ...new Set(masterPlayers.map(p => p.team).filter(Boolean))];
  const displayedPlayers = masterPlayers.filter(p => {
    if (filterTeam !== "ALL" && p.team !== filterTeam) return false;
    if (filterRole !== "ALL" && p.role?.toUpperCase() !== filterRole) return false;
    if (search && !p.name?.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const groupedByTeam = {};
  displayedPlayers.forEach(p => {
    const t = p.team || "Unknown";
    if (!groupedByTeam[t]) groupedByTeam[t] = [];
    groupedByTeam[t].push(p);
  });

  return (
    <div style={{ ...S.page, maxWidth: "1300px" }}>
      <style>{GLOBAL_ADMIN_CSS}</style>
      <button onClick={() => navigate("/winrox11-super-admin/dashboard")} style={S.backBtn}>← Back to Dashboard</button>

      <div style={S.topBar}>
        <div>
          <div style={S.pageTitle}>👥 Master Players</div>
          <div style={S.pageSubtitle}>Squad Database — All Teams</div>
        </div>
        <div style={{ display: "flex", gap: "10px" }}>
          <button
            onClick={() => setShowCsv(!showCsv)}
            style={{ ...S.btnCancel, fontSize: "12px" }}
          >
            {showCsv ? "✕ Close CSV" : "📄 Bulk CSV Upload"}
          </button>
          <div style={{ ...S.badge(COLORS.indigo), padding: "8px 14px", fontSize: "12px" }}>
            {masterPlayers.length} Total Players
          </div>
        </div>
      </div>

      {/* CSV Upload Panel */}
      {showCsv && (
        <div style={{ ...S.card, border: `1px solid ${COLORS.amber}40`, marginBottom: "24px" }}>
          <div style={{ ...S.cardTitle, color: COLORS.amber }}>📄 Bulk CSV Upload</div>
          <div style={{ fontSize: "12px", color: COLORS.textMuted, marginBottom: "14px" }}>
            CSV columns: <code style={{ background: COLORS.inputBg, padding: "2px 6px", borderRadius: "4px", color: COLORS.indigoLight }}>name, team, role, credit, image</code>
            &nbsp; (role: BAT / BOWL / AR / WK)
          </div>
          <input
            ref={csvRef}
            type="file"
            accept=".csv"
            onChange={handleCsvChange}
            style={{ marginBottom: "14px", color: COLORS.text, fontSize: "13px" }}
          />
          {csvPreview && (
            <>
              <div style={{ fontSize: "12px", color: COLORS.green, marginBottom: "10px" }}>
                ✓ {csvPreview.length} players detected in CSV. Review below:
              </div>
              <div style={{ maxHeight: "200px", overflowY: "auto", background: COLORS.inputBg, borderRadius: "10px", padding: "12px", marginBottom: "14px" }}>
                {csvPreview.slice(0, 20).map((r, i) => (
                  <div key={i} style={{ fontSize: "12px", color: COLORS.textMid, padding: "4px 0", borderBottom: `1px solid ${COLORS.cardBorder}` }}>
                    {i + 1}. <strong>{r.name}</strong> · {r.team} · {r.role} · {r.credit || "9"} Cr
                  </div>
                ))}
                {csvPreview.length > 20 && <div style={{ fontSize: "11px", color: COLORS.textMuted, paddingTop: "8px" }}>...and {csvPreview.length - 20} more</div>}
              </div>
              <button
                onClick={handleCsvUpload}
                disabled={csvUploading}
                style={{ ...S.btnWarning, opacity: csvUploading ? 0.6 : 1, cursor: csvUploading ? "not-allowed" : "pointer" }}
              >
                {csvUploading ? "⌛ Uploading..." : `Upload ${csvPreview.length} Players`}
              </button>
            </>
          )}
        </div>
      )}

      {/* ADD / EDIT FORM */}
      <div style={S.card}>
        <div style={S.cardTitle}>{editingId ? "✏️ Edit Player" : "➕ Add New Player"}</div>
        <form onSubmit={handleSavePlayer}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px", marginBottom: "16px" }}>
            <div>
              <label style={S.label}>Player Name *</label>
              <input
                className="admin-input-focus"
                style={S.input}
                placeholder="e.g. Virat Kohli"
                value={playerData.name}
                onChange={e => setPlayerData({ ...playerData, name: e.target.value })}
                required
              />
            </div>
            <div>
              <label style={S.label}>Team Code *</label>
              <input
                className="admin-input-focus"
                style={S.input}
                placeholder="e.g. IND, MI, CSK"
                value={playerData.team}
                onChange={e => setPlayerData({ ...playerData, team: e.target.value.toUpperCase() })}
                required
              />
            </div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 2fr", gap: "16px", marginBottom: "16px" }}>
            <div>
              <label style={S.label}>Role</label>
              <select
                className="admin-input-focus"
                style={S.select}
                value={playerData.role}
                onChange={e => setPlayerData({ ...playerData, role: e.target.value })}
              >
                <option value="BAT">🏏 Batsman</option>
                <option value="BOWL">⚡ Bowler</option>
                <option value="AR">🌟 All Rounder</option>
                <option value="WK">🧤 Wicket Keeper</option>
              </select>
            </div>
            <div>
              <label style={S.label}>Credit Points</label>
              <input
                className="admin-input-focus"
                type="number"
                step="0.5"
                min="1"
                max="15"
                style={S.input}
                value={playerData.credit}
                onChange={e => setPlayerData({ ...playerData, credit: parseFloat(e.target.value) })}
                required
              />
            </div>
            <div>
              <label style={S.label}>🔗 Photo URL (optional)</label>
              <input
                className="admin-input-focus"
                style={S.input}
                placeholder="https://example.com/player.jpg"
                value={playerData.image}
                onChange={e => setPlayerData({ ...playerData, image: e.target.value })}
              />
              <ImagePreview url={playerData.image} name={playerData.name} />
            </div>
          </div>
          <div style={S.btnRow}>
            {editingId && (
              <button
                type="button"
                style={S.btnCancel}
                onClick={() => {
                  setEditingId(null);
                  setPlayerData({ name: "", role: "BAT", team: playerData.team, credit: 9.0, image: "" });
                }}
              >
                Cancel
              </button>
            )}
            <button
              type="submit"
              disabled={saving}
              style={{ ...(editingId ? S.btnWarning : S.btnPrimary), opacity: saving ? 0.7 : 1, cursor: saving ? "not-allowed" : "pointer" }}
            >
              {saving ? "Saving..." : (editingId ? "Update Player" : "Add to Database")}
            </button>
          </div>
        </form>
      </div>

      {/* FILTERS */}
      <div style={{ display: "flex", gap: "12px", marginBottom: "20px", flexWrap: "wrap", alignItems: "center" }}>
        <input
          className="admin-input-focus"
          style={{ ...S.input, maxWidth: "260px", height: "42px" }}
          placeholder="🔍 Search players..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <select value={filterTeam} onChange={e => setFilterTeam(e.target.value)} style={{ ...S.select, width: "160px", height: "42px" }}>
          {uniqueTeams.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
        <select value={filterRole} onChange={e => setFilterRole(e.target.value)} style={{ ...S.select, width: "160px", height: "42px" }}>
          <option value="ALL">All Roles</option>
          <option value="BAT">Batsman</option>
          <option value="BOWL">Bowler</option>
          <option value="AR">All Rounder</option>
          <option value="WK">WK</option>
        </select>
        <div style={{ marginLeft: "auto", fontSize: "13px", color: COLORS.textMuted }}>
          Showing <strong style={{ color: COLORS.text }}>{displayedPlayers.length}</strong> of {masterPlayers.length}
        </div>
      </div>

      {/* PLAYER LIST */}
      {loading ? (
        <div style={S.loading}>Loading players...</div>
      ) : displayedPlayers.length === 0 ? (
        <div style={S.empty}>No players found. Try adjusting your filters.</div>
      ) : (
        <div style={{ animation: "fadeIn 0.3s ease" }}>
          {Object.entries(groupedByTeam).map(([team, players]) => (
            <div key={team} style={{ marginBottom: "28px" }}>
              <div style={{
                fontSize: "12px", fontWeight: 700, color: COLORS.textMid,
                letterSpacing: "1.5px", textTransform: "uppercase",
                marginBottom: "12px",
                display: "flex", alignItems: "center", gap: "10px"
              }}>
                <span style={{ background: COLORS.indigo + "20", border: `1px solid ${COLORS.indigo}30`, padding: "3px 10px", borderRadius: "6px", color: COLORS.indigoLight }}>
                  {team}
                </span>
                <span style={{ color: COLORS.textDim }}>{players.length} players</span>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: "12px" }}>
                {players.map(p => {
                  const rc = getRoleColor(p.role);
                  return (
                    <div
                      key={p.id}
                      className="admin-card-hover"
                      style={{
                        background: COLORS.card,
                        border: `1px solid ${COLORS.cardBorder}`,
                        borderRadius: "14px",
                        padding: "14px",
                        display: "flex",
                        alignItems: "center",
                        gap: "14px",
                        position: "relative",
                        transition: "all 0.15s",
                      }}
                    >
                      <div style={{ position: "absolute", top: "10px", right: "10px", display: "flex", gap: "4px" }}>
                        <button className="admin-btn-hover" style={S.miniBtn} onClick={() => handleEditClick(p)} title="Edit">✏️</button>
                        <button className="admin-btn-hover" style={{ ...S.miniBtn, color: COLORS.red }} onClick={() => handleDeletePlayer(p.id, p.name)} title="Delete">🗑️</button>
                      </div>
                      {p.image && isValidUrl(p.image) ? (
                        <img
                          src={p.image}
                          alt={p.name}
                          style={{ width: "44px", height: "44px", borderRadius: "50%", objectFit: "cover", border: `2px solid ${COLORS.cardBorder}`, flexShrink: 0 }}
                          onError={(e) => { e.target.style.display = "none"; }}
                        />
                      ) : (
                        <div style={{ width: "44px", height: "44px", borderRadius: "50%", background: rc + "15", border: `2px solid ${rc}30`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: "16px", flexShrink: 0 }}>
                          {p.role === "BAT" ? "🏏" : p.role === "BOWL" ? "⚡" : p.role === "WK" ? "🧤" : "🌟"}
                        </div>
                      )}
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <div style={{ fontSize: "14px", fontWeight: 700, color: COLORS.text, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", paddingRight: "50px" }}>
                          {p.name}
                        </div>
                        <div style={{ display: "flex", gap: "6px", alignItems: "center", marginTop: "5px", flexWrap: "wrap" }}>
                          <span style={{ ...S.badge(rc), padding: "2px 8px" }}>{p.role}</span>
                          <span style={{ fontSize: "11px", color: COLORS.textMuted, fontWeight: 600 }}>{p.credit} Cr</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const AddPlayer = () => (
  <ToastProvider>
    <AddPlayerInner />
  </ToastProvider>
);

export default AddPlayer;
